// Lex theme registry — the curated theme layer of the Slack-model theme
// system (plan: EDGELEX_THEME_SYSTEM_SLACK_MODEL_IMPLEMENTATION_PLAN_2026-08-01).
//
// A theme is a tiny seed (accent + optional surface palette + frame tint),
// never a palette dump: everything visual derives from existing --lex-*
// machinery (accent ramp, neutral ramps, and the [data-lex-theme] frame-tint
// rules in lex-theme.css). Applying a theme only writes ordinary appearance
// preferences — so the Custom tab, admin defaults, and persistence all keep
// working unchanged.
//
// Every curated theme × both modes is contrast-validated in
// src/utils/__tests__/themeGenerator.test.ts. Add a theme here and that gate
// runs against it automatically.

import type { AccentColor, NeutralBase, VisionMode } from './appearance';
import { ACCENT_COLOR_OPTIONS } from './appearance';

export type LexThemeKind = 'named' | 'single-color' | 'vision-assistive';

export interface LexThemeFrameTint {
  light: number; // percent of accent mixed into light-mode frame surfaces
  dark: number; // percent of accent mixed into dark-mode frame surfaces
}

export interface LexTheme {
  id: string;
  label: string;
  description: string;
  kind: LexThemeKind;
  accentColor: AccentColor;
  /** Seed hex when accentColor is 'custom'. */
  accentCustomHex?: string;
  /**
   * Surface palette the theme pins. Omitted for single-color themes, which
   * respect the user's current surface palette (they recolor accent + frame
   * only — the Slack "single color" behavior).
   */
  neutralBase?: NeutralBase;
  frameTint: LexThemeFrameTint;
  visionMode: VisionMode;
  /** Card swatch for the gallery UI. */
  swatch: string;
}

const NO_TINT: LexThemeFrameTint = { light: 0, dark: 0 };
const STANDARD_TINT: LexThemeFrameTint = { light: 8, dark: 14 };

/**
 * Named themes — each is a validated light+dark pair. Oxblood is the current
 * product look formalized as a theme (zero frame tint → zero visual delta for
 * existing users).
 */
const NAMED_THEMES: LexTheme[] = [
  {
    id: 'oxblood',
    label: 'Oxblood',
    description: 'The EdgeLex signature — oxblood accent on warm surfaces.',
    kind: 'named',
    accentColor: 'oxblood',
    neutralBase: 'warm',
    frameTint: NO_TINT,
    visionMode: 'none',
    swatch: '#7e1f2c',
  },
  {
    id: 'chambers',
    label: 'Chambers',
    description: 'Deep court blue with a tinted frame on cool slate surfaces.',
    kind: 'named',
    accentColor: 'custom',
    accentCustomHex: '#1e4e8c',
    neutralBase: 'slate',
    frameTint: STANDARD_TINT,
    visionMode: 'none',
    swatch: '#1e4e8c',
  },
  {
    id: 'verdigris',
    label: 'Verdigris',
    description: 'Aged-bronze teal with a tinted frame on charcoal surfaces.',
    kind: 'named',
    accentColor: 'custom',
    accentCustomHex: '#0f766e',
    neutralBase: 'charcoal',
    frameTint: STANDARD_TINT,
    visionMode: 'none',
    swatch: '#0f766e',
  },
  {
    id: 'gaslight',
    label: 'Gaslight',
    description: 'Antique gold on warm surfaces, with a gently gilded frame.',
    kind: 'named',
    accentColor: 'custom',
    accentCustomHex: '#8a611d',
    neutralBase: 'warm',
    frameTint: { light: 6, dark: 12 },
    visionMode: 'none',
    swatch: '#8a611d',
  },
  {
    id: 'violet-ink',
    label: 'Violet Ink',
    description: 'Lex violet on crisp graphite surfaces with an inked frame.',
    kind: 'named',
    accentColor: 'custom',
    accentCustomHex: '#7c5cd6',
    neutralBase: 'graphite',
    frameTint: { light: 6, dark: 12 },
    visionMode: 'none',
    swatch: '#7c5cd6',
  },
  {
    id: 'midnight-docket',
    label: 'Midnight Docket',
    description: 'Indigo on deep blue-black surfaces with a strong frame wash.',
    kind: 'named',
    accentColor: 'indigo',
    neutralBase: 'midnight',
    frameTint: { light: 8, dark: 16 },
    visionMode: 'none',
    swatch: '#6366f1',
  },
];

/**
 * Single-color themes — Slack's "Single color" row. One accent seed generates
 * the whole look (accent ramp + frame tint) on the user's current surface
 * palette.
 */
const SINGLE_COLOR_ACCENTS: AccentColor[] = [
  'blue',
  'indigo',
  'violet',
  'rose',
  'red',
  'orange',
  'green',
  'emerald',
  'teal',
  'cyan',
];

const SINGLE_COLOR_THEMES: LexTheme[] = SINGLE_COLOR_ACCENTS.map((accent) => {
  const option = ACCENT_COLOR_OPTIONS.find((candidate) => candidate.id === accent);
  const label = option ? option.label : accent;
  return {
    id: `mono-${accent}`,
    label,
    description: `${label} accent and frame on your current surface palette.`,
    kind: 'single-color',
    accentColor: accent,
    frameTint: STANDARD_TINT,
    visionMode: 'none',
    swatch: option ? option.colorValue : '#3b82f6',
  };
});

/**
 * Vision-assistive themes — mirror Slack's CVD presets. Besides the seed,
 * these set data-vision, which re-maps the status colors in lex-theme.css to
 * confusion-safe axes (blue/orange for protan/deutan, red/cyan for tritan).
 */
const VISION_THEMES: LexTheme[] = [
  {
    id: 'vision-protan-deutan',
    label: 'Protanopia & Deuteranopia',
    description: 'Blue/orange status colors — safe for red-green color vision.',
    kind: 'vision-assistive',
    accentColor: 'blue',
    frameTint: NO_TINT,
    visionMode: 'protan-deutan',
    swatch: '#3b82f6',
  },
  {
    id: 'vision-tritan',
    label: 'Tritanopia',
    description: 'Red/cyan status colors — safe for blue-yellow color vision.',
    kind: 'vision-assistive',
    accentColor: 'rose',
    frameTint: NO_TINT,
    visionMode: 'tritan',
    swatch: '#f43f5e',
  },
];

export const LEX_THEMES: LexTheme[] = [...NAMED_THEMES, ...SINGLE_COLOR_THEMES, ...VISION_THEMES];

/** Sentinel meaning "no preset — the user drives individual controls". */
export const LEX_THEME_CUSTOM = 'custom';

export const DEFAULT_LEX_THEME = 'oxblood';

export const getLexTheme = (id: string | null | undefined): LexTheme | null =>
  LEX_THEMES.find((theme) => theme.id === id) ?? null;

/** Unknown/legacy stored values resolve to 'custom' — never to a wrong theme. */
export const normalizeLexTheme = (id: string | null | undefined): string => {
  if (id === LEX_THEME_CUSTOM) return LEX_THEME_CUSTOM;
  return getLexTheme(id) ? (id as string) : LEX_THEME_CUSTOM;
};

export const getThemesByKind = (kind: LexThemeKind): LexTheme[] =>
  LEX_THEMES.filter((theme) => theme.kind === kind);

/** The theme's effective accent seed hex (palette value or custom hex). */
export const getThemeSeedHex = (theme: LexTheme): string => {
  if (theme.accentColor === 'custom') return theme.accentCustomHex ?? '#3b82f6';
  const option = ACCENT_COLOR_OPTIONS.find((candidate) => candidate.id === theme.accentColor);
  return option ? option.colorValue : '#3b82f6';
};
